import React, { useEffect, useState } from 'react';

import { Button, Colors } from '@cognite/cogs.js';
import { Icon } from 'components/Icon';
import { getFlow, saveFlow, saveTenantFlow } from '@cognite/auth-utils';
import styled from 'styled-components/macro';

import { PublicClientApplication } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';

import {
  usePostOAuthLogin,
  publicClient,
  TENANT_LOCALSTORAGE_KEY,
  ACCOUNT_LOCALSTORAGE_KEY,
} from 'store/auth';
import {
  EnvSetting,
  NEW_LOGIN_STORAGE_KEY,
  ProjectSelector,
} from 'pages/LoginPage/ProjectSelector';
import { AuthProviders, Cluster } from './types';
import LoginWithAzure from './LoginWithAzure';
import LegacyLoginInput from './LegacyLoginInput';
import ClusterSelect from './ClusterSelect';
import ProjectSelect from './ProjectSelect';

type ClustersInfo = {
  label: string;
  options: Cluster[];
};

const LoginType = { NEW_LOGIN: 'NEW_LOGIN', CUSTOM_LOGIN: 'CUSTOM_LOGIN' };
const LOGIN_TYPE_STORAGE_KEY = 'loginType';
type Props = {
  getCluster: () => string;
  updateCluster: (cluster: Cluster) => void;
  clusters: ClustersInfo[];
  move: (project: string, method?: string, tenant?: string) => void;
};

const getClusterInfo = (cluster: string, clusters: Cluster[]) =>
  clusters.find((x) => x.value === cluster);

const accumulateClustersOptions = (clustersInfo: ClustersInfo[]) =>
  clustersInfo.reduce((acc: Cluster[], c) => [...acc, ...c.options], []);

// oidc client in sdk adds some items in localstorage, contain sensitive info
const clearOidcItems = () => {
  Object.keys(localStorage).forEach((key: string) => {
    if (key.startsWith('oidc')) {
      window.localStorage.removeItem(key);
    }
  });
};

export const TenantSelector = ({
  getCluster,
  updateCluster,
  clusters,
  move,
}: Props) => {
  const postOAuthLogin = usePostOAuthLogin();

  const cluster = getCluster();
  const clustersOptions = accumulateClustersOptions(clusters);
  const selectedClusterInfo = getClusterInfo(cluster, clustersOptions);

  const [provider, setProvider] = useState<
    AuthProviders.Azure | AuthProviders.Auth0
  >(selectedClusterInfo?.type || AuthProviders.Azure);

  const onClusterChange = (e: Cluster) => {
    setProvider(e?.type ?? AuthProviders.Azure);
    setCluster(e);
  };

  const setCluster = updateCluster;
  let { instance } = useMsal();

  const [gotToken, setGotToken] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [selectedEnvSetting, setSelectedEnvSetting] = useState<EnvSetting>();
  const [loginType, setLoginType] = useState(
    window.localStorage.getItem(LOGIN_TYPE_STORAGE_KEY) || LoginType.NEW_LOGIN
  );

  const toggleLoginType = () => {
    const type =
      loginType === LoginType.NEW_LOGIN
        ? LoginType.CUSTOM_LOGIN
        : LoginType.NEW_LOGIN;
    window.localStorage.setItem(LOGIN_TYPE_STORAGE_KEY, type);
    setLoginType(type);
  };

  const clustersWithLegacyAuth = clustersOptions.filter((c) => c.legacyAuth);
  const clustersWithAAD = clustersOptions.filter((c) => !c.disableAad);

  const onEnvSettingSelected = (envSetting: EnvSetting) => {
    const selectedCluster = clusters[
      envSetting.clusterGroupNumber
    ].options.find((x) => x.value === envSetting.clusterId)!;
    onClusterChange(selectedCluster);
    setSelectedEnvSetting(envSetting);
  };

  const login = (directory?: string) => {
    window.localStorage.setItem(LOGIN_TYPE_STORAGE_KEY, loginType);
    setIsInitializing(true);
    setHasError(false);
    window.localStorage.removeItem('@cognite/sdk:accountLocalId');
    window.localStorage.removeItem(ACCOUNT_LOCALSTORAGE_KEY);
    window.sessionStorage.setItem('login-clicked', 'true');

    instance = new PublicClientApplication({ ...instance.getConfiguration() });
    instance.loginRedirect({
      prompt: 'select_account',
      scopes: ['openid', 'offline_access', 'profile'],
      authority: `https://login.microsoftonline.com/${directory}`,
    });
  };

  const logout = () => {
    window.localStorage.removeItem('@cognite/sdk:accountLocalId');
    window.localStorage.removeItem('cognite__auth__v4_flow');
    window.localStorage.removeItem(ACCOUNT_LOCALSTORAGE_KEY);
    window.localStorage.removeItem(NEW_LOGIN_STORAGE_KEY);
    window.localStorage.removeItem(TENANT_LOCALSTORAGE_KEY);

    window.sessionStorage.removeItem('login-clicked');
    clearOidcItems();
    setGotToken(false);
  };

  useEffect(() => {
    setIsInitializing(true);

    publicClient
      .handleRedirectPromise()
      .then((r) => {
        if (r)
          localStorage.setItem(
            ACCOUNT_LOCALSTORAGE_KEY,
            r?.account?.localAccountId || ''
          );
        const { flow, options } = getFlow();
        if (flow === 'AZURE_AD') {
          const accountId =
            localStorage.getItem(ACCOUNT_LOCALSTORAGE_KEY) || '';
          if (!accountId) {
            setIsInitializing(false);
            return;
          }
          const account = publicClient.getAccountByLocalId(accountId);
          if (!account) throw new Error('no user found');

          publicClient
            .acquireTokenSilent({
              account,
              authority: `https://login.microsoftonline.com/${options?.directory}`,
              scopes: [
                `https://${
                  selectedClusterInfo?.cluster || 'api'
                }.cognitedata.com/IDENTITY`,
                `openid`,
                'offline_access',
                'profile',
              ],
            })
            .then((x) => {
              postOAuthLogin(undefined, x.accessToken)
                .then(() => {
                  setHasError(false);
                  setGotToken(true);
                })
                .catch(() => {
                  setHasError(true);
                })
                .finally(() => {
                  setIsInitializing(false);
                });
            })
            .catch(() => {
              setHasError(true);
              setIsInitializing(false);
            });
        } else setIsInitializing(false);
      })
      .catch(() => {
        setHasError(true);
        setIsInitializing(false);
      });
  }, []);

  useEffect(() => {
    if (selectedEnvSetting) {
      saveFlow('AZURE_AD', { directory: selectedEnvSetting?.audience.trim() });
      login(selectedEnvSetting.audience);
    }
  }, [selectedEnvSetting]);

  return (
    <Card>
      <ContentWrapper>
        {loginType === LoginType.NEW_LOGIN ? (
          <ProjectSelector
            isInitializing={isInitializing}
            isAuthenticated={gotToken}
            move={move}
            hasError={hasError}
            logout={logout}
            onProjectSelect={onEnvSettingSelected}
          />
        ) : (
          <>
            <ClusterSelect
              cluster={cluster}
              clusters={clusters}
              onClusterChange={(env) => {
                onClusterChange(env);
              }}
            />

            {gotToken && <ProjectSelect move={move} />}

            {!isInitializing &&
            window?.sessionStorage?.getItem('login-clicked') === 'true' &&
            (hasError || !gotToken) ? (
              <>
                <div style={{ margin: '10px 0' }}>
                  <Icon
                    type="Error"
                    style={{ color: 'var(--cogs-danger)', marginRight: 10 }}
                  />
                  An error occurred when logging into the environment above.
                  Make sure you are a user that belongs in an Azure tenant that
                  has the cluster installed.
                </div>

                {clustersWithLegacyAuth.find((c) => c.value === cluster) && (
                  <div style={{ margin: '10px 0' }}>
                    <strong>
                      It is still possible to specify the project name below.
                    </strong>{' '}
                    This error is not relevant for that sign in flow.
                  </div>
                )}
              </>
            ) : null}
            {clustersWithAAD.map((c) => c.value).includes(cluster) && (
              <LoginWithAzure
                login={login}
                logout={logout}
                isInitializing={isInitializing}
                isAuthenticated={gotToken}
                provider={provider}
              />
            )}
            {clustersWithLegacyAuth.map((c) => c.value).includes(cluster) && (
              <LegacyContainer>
                <LegacyLoginInput
                  cluster={selectedClusterInfo?.cluster || ''}
                  onTenantSelected={(tenant) => {
                    saveTenantFlow(
                      { tenant, env: selectedClusterInfo?.cluster },
                      'COGNITE_AUTH'
                    );
                    move(tenant, 'tenant');
                  }}
                />
              </LegacyContainer>
            )}
          </>
        )}
        <StyledButton
          htmlType="button"
          type="ghost"
          onClick={toggleLoginType}
          style={{ marginTop: 10, marginBottom: 5 }}
        >
          {loginType === LoginType.NEW_LOGIN
            ? 'Advanced login options'
            : 'Simple login'}
        </StyledButton>
      </ContentWrapper>
      <HelpLink
        href="https://docs.cognite.com/cdf/access/troubleshooting/login.html"
        target="_blank"
        rel="noopener noreferrer"
      >
        Help <HelpLinkIcon type="ExternalLink" />
      </HelpLink>
    </Card>
  );
};

const LegacyContainer = styled.div`
  margin-top: 30px;
  padding-top: 30px;
  border-top: 1px solid var(--cogs-greyscale-grey3);
`;

const Card = styled.div`
  max-width: 100%;
  background: #fff;
  border-radius: 4px;
  box-sizing: border-box;
  box-shadow:
    0px 16.8443px 50.5328px rgba(0, 0, 0, 0.1),
    0px 13.4754px 20.2131px rgba(0, 0, 0, 0.07);
`;

const ContentWrapper = styled.div`
  padding: 16px 32px 0;

  button {
    width: 100%;
    height: 40px;
  }

  .content {
    margin-top: 40px;
  }
`;

const HelpLink = styled.a`
  background-color: #fafafa;
  color: var(--cogs-text-color);
  padding: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-bottom-left-radius: 4px;
  border-bottom-right-radius: 4px;
`;

const HelpLinkIcon = styled(Icon)`
  margin-left: 5px;
`;

const StyledButton = styled(Button)`
  &.cogs-btn-disabled {
    color: #ffffff;
    background: var(--cogs-primary);
    opacity: 0.7;
    cursor: not-allowed !important;
    &:hover {
      background: var(--cogs-primary);
      color: #ffffff;
    }
  }
`;
